import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  useTheme,
  Stack,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Pagination,
  Divider,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import {
  Calendar,
  Users,
  Plus,
  CheckCircle,
  CalendarCheck,
  Gear,
  Eye,
  CalendarBlank,
  X,
  SlidersHorizontal,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBookingGuestDisplayName, getBookingGuestEmailLine } from '../../utils/unifiedBookingDisplay';
import { useBusiness } from '../../context/BusinessContext';
import { usePermissions } from '../../hooks/usePermissions';
import UnifiedCalendar from './UnifiedCalendar';
import api from '../../utils/api';
import CalendarSyncSettings from './CalendarSyncSettings';
import EventForm from './EventForm';
import useThemeStyles from '../../theme/useThemeStyles';

function formatLocalYmd(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function UnifiedBookingDashboard() {
  const styles = useThemeStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness } = useBusiness();
  const { _can } = usePermissions();

  // Week navigation helpers
  function getWeekRange(offset = 0) {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + offset * 7);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }

  function _formatWeekRange(range) {
    if (!range) return '';
    const { start, end } = range;
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endLabel = end.toLocaleDateString(
      undefined, 
      sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' }
    );
    const yearLabel = start.getFullYear() === end.getFullYear() ? start.getFullYear() : `${start.getFullYear()} / ${end.getFullYear()}`;
    return `${startLabel} - ${endLabel}, ${yearLabel}`;
  }
  
  // Always show calendar view - no view switching needed
  const activeView = 'calendar';
  const [_stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
    upcomingBookings: 0,
    appointmentTypes: 0,
  });
  const [allBookings, setAllBookings] = useState([]);
  const [thisWeekBookings, setThisWeekBookings] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [_currentWeekRange, setCurrentWeekRange] = useState(getWeekRange(0));
  const [pendingRequests, setPendingRequests] = useState([]);
  const [_rescheduledBookings, setRescheduledBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState('');
  const [_upcomingStatusFilter, _setUpcomingStatusFilter] = useState('all'); // all | confirmed | cancelled
  
  // Booking details modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  
  // Adjustment modal state
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [rescheduleStaffMemberId, setRescheduleStaffMemberId] = useState('');
  const [rescheduleSelectedDate, setRescheduleSelectedDate] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false);
  const [rescheduleStaffMembers, setRescheduleStaffMembers] = useState([]);
  const [adjustmentSubmitError, setAdjustmentSubmitError] = useState('');
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const rescheduleBookingIdRef = useRef(null);
  
  // Team member filter state
  const [_teamMembers, setTeamMembers] = useState([]);
  const [_selectedTeamMemberFilter, _setSelectedTeamMemberFilter] = useState('');
  
  // Team member editing state
  const [editTeamMembersOpen, setEditTeamMembersOpen] = useState(false);
  const [availableTeamMembersForEdit, setAvailableTeamMembersForEdit] = useState([]);
  const [selectedTeamMembersForEdit, setSelectedTeamMembersForEdit] = useState([]);
  const [editShiftWindowsByMember, setEditShiftWindowsByMember] = useState({});
  const [editLocationType, setEditLocationType] = useState('on_site');
  const [editLocationAddress, setEditLocationAddress] = useState('');
  const [editVideoLink, setEditVideoLink] = useState('');
  const [editSupplierNotes, setEditSupplierNotes] = useState('');
  const [editBookingSaving, setEditBookingSaving] = useState(false);
  const [editBookingError, setEditBookingError] = useState('');
  const [editBookingToast, setEditBookingToast] = useState({ open: false, message: '' });

  // Calendar sync settings state
  const [showSyncSettings, setShowSyncSettings] = useState(false);

  // Events state
  const [events, setEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [_upcomingEvents, setUpcomingEvents] = useState([]);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  
  // Create New dropdown menu state
  const [createNewAnchorEl, setCreateNewAnchorEl] = useState(null);
  const _createNewMenuOpen = Boolean(createNewAnchorEl);
  
  // Pending Appointments modal state
  const [pendingAppointmentsModalOpen, setPendingAppointmentsModalOpen] = useState(false);
  const [pendingModalPage, setPendingModalPage] = useState(1);
  const [upcomingModalPage, setUpcomingModalPage] = useState(1);
  const bookingsModalPageSize = 10;

  // Appointment types guard for Create New → Appointment
  const [appointmentTypesCount, setAppointmentTypesCount] = useState(null);
  const [noAppointmentTypesModalOpen, setNoAppointmentTypesModalOpen] = useState(false);

  // Approve-booking flow state: allowedTeamMembers + inline shift prompt
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [approveAllowed, setApproveAllowed] = useState([]);
  const [approveHasRestriction, setApproveHasRestriction] = useState(false);
  const [approveSelectedId, setApproveSelectedId] = useState('');
  const [approveShiftStart, setApproveShiftStart] = useState('');
  const [approveShiftEnd, setApproveShiftEnd] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  const [crmAfterApproveOpen, setCrmAfterApproveOpen] = useState(false);
  const [crmAfterApproveContext, setCrmAfterApproveContext] = useState(null);
  const [crmAfterApproveSubmitting, setCrmAfterApproveSubmitting] = useState(false);
  const [crmAfterApproveError, setCrmAfterApproveError] = useState('');

  useEffect(() => {
    if (selectedBusiness?.business._id) {
      fetchBookingData();
      fetchEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness]);

  useEffect(() => {
    if (!pendingAppointmentsModalOpen) return;
    setPendingModalPage(1);
    setUpcomingModalPage(1);
  }, [pendingAppointmentsModalOpen]);

  const fetchEvents = async () => {
    try {
      const response = await api.get(`/api/events/business/${selectedBusiness?.business?._id || selectedBusiness?._id}`);
      const evts = response.data.events || [];
      setEvents(evts);
      const { start, end } = getWeekRange(weekOffset);
      const upcoming = evts
        .filter(e => {
          const startDate = new Date(e.startTime);
          return startDate >= start && startDate <= end;
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setUpcomingEvents(upcoming);
    } catch (error) {}
  };

  // Recalculate visible week bookings when navigating weeks or after data fetch
  useEffect(() => {
    if (!allBookings) return;
    const { start, end } = getWeekRange(weekOffset);

    const weekBookings = allBookings.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return bookingDate >= start &&
             bookingDate <= end;
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    setThisWeekBookings(weekBookings);
    const evts = events || [];
    const upcoming = evts
      .filter(e => {
        const d = new Date(e.startTime);
        return d >= start && d <= end;
      })
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    setUpcomingEvents(upcoming);
    setCurrentWeekRange({ start, end });
  }, [weekOffset, allBookings, events]);

  const fetchBookingData = async () => {
    setLoading(true);
    try {
      // Fetch bookings for the selected business
      const params = {
        type: 'supplier',
        limit: 1000 // Get all bookings to calculate stats
      };
      
      // Pass businessId if available
      if (selectedBusiness?.business?._id) {
        params.businessId = selectedBusiness.business._id;
      }
      
      const businessId = selectedBusiness?.business?._id;
      const [response, serviceTypesResponse] = await Promise.all([
        api.get('/api/unified-bookings', { params }),
        businessId
          ? api.get('/api/service-types', { params: { businessId } }).catch(() => ({ data: { serviceTypes: [] } }))
          : Promise.resolve({ data: { serviceTypes: [] } }),
      ]);

      const bookings = response.data.bookings;
      const appointmentTypeTotal = serviceTypesResponse.data.serviceTypes?.length || 0;
      setAppointmentTypesCount(appointmentTypeTotal);
      const now = new Date();
      
      // Calculate start and end of current week (Sunday to Saturday)
      const { start: startOfWeek, end: endOfWeek } = getWeekRange(0);

      // Calculate statistics
      const stats = {
        totalBookings: bookings.length,
        pendingBookings: bookings.filter(b => b.status === 'pending').length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        completedBookings: bookings.filter(b => b.status === 'completed').length,
        upcomingBookings: bookings.filter(b => 
          b.status === 'confirmed' && new Date(b.startTime) > now
        ).length,
        appointmentTypes: appointmentTypeTotal,
      };

      // Filter this week's bookings (within current week, include cancelled for filtering later)
      const weekBookings = bookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        // Include bookings that are within the week
        return bookingDate >= startOfWeek && 
               bookingDate <= endOfWeek;
      }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      // Filter pending requests (only future bookings)
      const pending = bookings.filter(b => 
        b.status === 'pending' && new Date(b.startTime) >= now
      ).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      // Filter missed appointments (unapproved past bookings)
      const missed = bookings.filter(b => 
        b.status === 'pending' && new Date(b.startTime) < now
      ).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      setStats(stats);
      setAllBookings(bookings);
      setWeekOffset(0);
      setCurrentWeekRange({ start: startOfWeek, end: endOfWeek });
      setThisWeekBookings(weekBookings);
      setPendingRequests(pending);
      setRescheduledBookings(missed);
      
      // Fetch team members for filter
      await fetchTeamMembers();
    } catch (error) {
      // Fallback to empty data if API fails
      setAppointmentTypesCount(0);
      setStats({
        totalBookings: 0,
        pendingBookings: 0,
        confirmedBookings: 0,
        completedBookings: 0,
        upcomingBookings: 0,
        appointmentTypes: 0,
      });
      setThisWeekBookings([]);
      setPendingRequests([]);
      setRescheduledBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const pendingModalPageCount = Math.max(
    Math.ceil(pendingRequests.length / bookingsModalPageSize),
    1
  );
  const upcomingModalPageCount = Math.max(
    Math.ceil(thisWeekBookings.length / bookingsModalPageSize),
    1
  );
  const pendingRequestsPageItems = pendingRequests.slice(
    (pendingModalPage - 1) * bookingsModalPageSize,
    pendingModalPage * bookingsModalPageSize
  );
  const upcomingBookingsPageItems = thisWeekBookings.slice(
    (upcomingModalPage - 1) * bookingsModalPageSize,
    upcomingModalPage * bookingsModalPageSize
  );

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get(`/api/business-members/business/${selectedBusiness.business._id}`);
      setTeamMembers(response.data.members || []);
    } catch (error) {}
  };


  const _handleWeekChange = (direction) => {
    setWeekOffset((prev) => prev + direction);
  };

  // Memoize callbacks to prevent UnifiedCalendar infinite re-renders
  const handleCalendarEventClick = useCallback((evt) => {
    setSelectedEvent(evt);
    setShowEventForm(true);
  }, []);

  const handleCalendarBookingChanged = useCallback(() => {
    fetchBookingData();
    setCalendarRefresh(prev => prev + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, []);

  const handleBookingAction = async (bookingId, action) => {
    try {
      const payload = { 
        status: action,
        notifyClient: true // Always notify client of status changes
      };
      
      // Add adjustment notes for rescheduled bookings
      if (action === 'rescheduled' && adjustmentNotes) {
        payload.notes = adjustmentNotes;
      }
      
      await api.put(`/api/unified-bookings/${bookingId}/status`, payload);
      fetchBookingData(); // Refresh data
      
      // Close modal if open
      if (bookingModalOpen) {
        setBookingModalOpen(false);
        setSelectedBooking(null);
        setAdjustmentNotes('');
      }
    } catch (error) {}
  };

  const handleAdjustClick = (booking = null) => {
    const target = booking || selectedBooking;
    const id = target ? (target._id || target.id) : null;
    if (id) {
      setRescheduleBookingId(id);
      rescheduleBookingIdRef.current = id;
    } else {
      setRescheduleBookingId(null);
      rescheduleBookingIdRef.current = null;
    }
    if (booking) setSelectedBooking(booking);
    setAdjustmentSubmitError('');
    setAdjustmentModalOpen(true);
    setRescheduleStaffMemberId('');
    setRescheduleSelectedDate(null);
    setRescheduleSlots([]);
    setNewStartTime('');
    setNewEndTime('');
    const targetBooking = booking || selectedBooking;
    const businessId = selectedBusiness?.business?._id || targetBooking?.business?._id || targetBooking?.business;
    if (businessId) {
      api.get(`/api/supplier/staff-members?businessId=${businessId}`)
        .then((res) => setRescheduleStaffMembers(res.data.staffMembers || []))
        .catch(() => setRescheduleStaffMembers([]));
    } else {
      setRescheduleStaffMembers([]);
    }
  };

  const fetchRescheduleSlots = useCallback(async () => {
    if (!rescheduleStaffMemberId || !rescheduleSelectedDate || !selectedBooking) return;
    const businessId = selectedBusiness?.business?._id || selectedBooking.business?._id || selectedBooking.business;
    if (!businessId) return;
    setLoadingRescheduleSlots(true);
    try {
      const duration = selectedBooking.serviceType?.duration || 60;
      const dateParam = formatLocalYmd(rescheduleSelectedDate);

      const openRes = await api.get(`/api/opening-hours/${businessId}/availability/${dateParam}`, {
        params: { duration },
      });

      const openingSlots = openRes.data?.availableSlots || [];

      const now = new Date();
      const slots = openingSlots
        .map((slot) => {
          return {
            startTime: slot.startTime,
            endTime: slot.endTime,
          };
        })
        .filter((slot) => new Date(slot.startTime) > now);

      setRescheduleSlots(slots);
    } catch {
      setRescheduleSlots([]);
    } finally {
      setLoadingRescheduleSlots(false);
    }
  }, [rescheduleStaffMemberId, rescheduleSelectedDate, selectedBooking, selectedBusiness]);

  useEffect(() => {
    if (rescheduleStaffMemberId && rescheduleSelectedDate && selectedBooking) {
      fetchRescheduleSlots();
    } else {
      setRescheduleSlots([]);
    }
  }, [rescheduleStaffMemberId, rescheduleSelectedDate, selectedBooking, fetchRescheduleSlots]);

  const handleAdjustmentSubmit = async () => {
    const bookingId = rescheduleBookingId || rescheduleBookingIdRef.current || selectedBooking?._id || selectedBooking?.id;
    if (!bookingId) return;
    setAdjustmentSubmitError('');
    setAdjustmentSubmitting(true);
    try {
      const payload = {
        status: 'rescheduled',
        newStartTime: newStartTime,
        newEndTime: newEndTime,
        notes: adjustmentReason,
        notifyClient: true, // Flag to send email notification
        ...(rescheduleStaffMemberId && {
          assignedTeamMembers: [rescheduleStaffMemberId],
          forceAssign: true
        })
      };

      await api.put(`/api/unified-bookings/${bookingId}/status`, payload);
      fetchBookingData(); // Refresh data

      setAdjustmentModalOpen(false);
      setAdjustmentNotes('');
      setNewStartTime('');
      setNewEndTime('');
      setAdjustmentReason('');
      setRescheduleStaffMemberId('');
      setRescheduleSelectedDate(null);
      setRescheduleSlots([]);
      setRescheduleBookingId(null);
      rescheduleBookingIdRef.current = null;
      // Fetch full booking so details modal shows updated data with all populated fields
      try {
        const fresh = await api.get(`/api/unified-bookings/${bookingId}`);
        if (fresh?.data) setSelectedBooking(fresh.data);
      } catch {
        setBookingModalOpen(false);
        setSelectedBooking(null);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to send reschedule request';
      setAdjustmentSubmitError(msg);
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  const handleViewBooking = async (booking) => {
    try {
      // Fetch fresh booking data from API to ensure we have staffMemberName and manualBookingDetails
      const response = await api.get(`/api/unified-bookings/${booking._id}`);
      const freshBooking = response.data;

      setSelectedBooking(freshBooking);
      setBookingModalOpen(true);
      setAdjustmentNotes('');
    } catch (error) {
      // Fallback to using the passed booking if API call fails
      setSelectedBooking(booking);
      setBookingModalOpen(true);
      setAdjustmentNotes('');
    }
  };

  const handleCloseModal = () => {
    setBookingModalOpen(false);
    setSelectedBooking(null);
    setAdjustmentNotes('');
  };

  const populateEditBookingFields = (booking) => {
    setEditLocationType(booking?.location?.type || 'on_site');
    setEditLocationAddress(booking?.location?.address || '');
    setEditVideoLink(booking?.location?.videoLink || '');
    setEditSupplierNotes(booking?.supplierNotes || '');
    setEditBookingError('');
  };

  const handleEditTeamMembers = async () => {
    if (!selectedBooking) return;

    const businessId = selectedBooking.business?._id || selectedBooking.business;

    try {
      const [allowedRes, staffRes] = await Promise.all([
        api.get(`/api/unified-bookings/${selectedBooking._id}/allowed-team-members`),
        api.get(`/api/supplier/staff-members?businessId=${businessId}`)
      ]);

      const allowed = allowedRes.data.allowedTeamMembers || [];
      const staffMembers = staffRes.data.staffMembers || [];
      setAvailableTeamMembersForEdit(allowed);

      const businessMemberToStaffId = new Map(
        staffMembers.map((sm) => {
          const bmId = sm.businessMember?._id || sm.businessMember;
          return [bmId?.toString?.() || String(bmId || ''), sm._id?.toString?.() || String(sm._id)];
        })
      );

      const currentAssignedStaffIds = (selectedBooking.assignedTeamMembers || []).map((assignment) => {
        const staffMemberId = assignment.staffMember?._id || assignment.staffMember;
        if (staffMemberId) return staffMemberId.toString();
        const memberId = assignment.member?._id || assignment.member;
        if (!memberId) return null;
        return businessMemberToStaffId.get(memberId.toString()) || null;
      }).filter(Boolean);

      const initialSelections = currentAssignedStaffIds.length > 0 ? currentAssignedStaffIds : [];
      setSelectedTeamMembersForEdit(initialSelections);

      const defaultStart = toTimeString(selectedBooking.startTime);
      const defaultEnd = toTimeString(selectedBooking.endTime);
      const shiftWindows = {};
      initialSelections.forEach((memberId) => {
        const memberMeta = allowed.find((member) => String(member.id) === String(memberId));
        if (memberId && memberMeta && !memberMeta.hasShiftCoveringBooking) {
          shiftWindows[memberId] = { start: defaultStart, end: defaultEnd };
        }
      });
      setEditShiftWindowsByMember(shiftWindows);

      populateEditBookingFields(selectedBooking);
      setEditTeamMembersOpen(true);
    } catch (error) {
      setError('Failed to load available team members');
    }
  };

  const bookingHasCoupleContact = (booking) => {
    if (!booking) return false;
    const manualEmail = booking.manualBookingDetails?.clientEmail;
    const clientEmail = booking.client?.email;
    return Boolean((manualEmail || clientEmail || '').trim());
  };

  const handleSaveBookingEdit = async (notifyClient = false) => {
    if (!selectedBooking) return;

    const trimmedVideoLink = (editVideoLink || '').trim();
    if (trimmedVideoLink && !/^https?:\/\//i.test(trimmedVideoLink)) {
      setEditBookingError('Enter a full video call URL starting with http:// or https://.');
      return;
    }

    const selectedStaffIds = selectedTeamMembersForEdit
      .map((memberId) => (memberId ? memberId.toString() : ''))
      .filter(Boolean);
    const uniqueSelectedStaffIds = [...new Set(selectedStaffIds)];

    if (uniqueSelectedStaffIds.length !== selectedStaffIds.length) {
      setEditBookingError('Each team member slot must have a different person assigned.');
      return;
    }

    setEditBookingError('');
    setEditBookingSaving(true);

    try {
      const bookingStart = new Date(selectedBooking.startTime);
      const bookingEnd = new Date(selectedBooking.endTime);
      const dayStart = new Date(bookingStart);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(bookingStart);
      dayEnd.setHours(23, 59, 59, 999);

      const memberMetaById = new Map(
        availableTeamMembersForEdit.map((member) => [String(member.id), member])
      );

      for (const memberId of uniqueSelectedStaffIds) {
        const memberMeta = memberMetaById.get(memberId);
        if (!memberMeta || memberMeta.hasShiftCoveringBooking) continue;

        const shiftWindow = editShiftWindowsByMember[memberId] || {};
        const shiftStart = combineBookingDayWithTime(selectedBooking.startTime, shiftWindow.start || toTimeString(bookingStart));
        const shiftEnd = combineBookingDayWithTime(selectedBooking.startTime, shiftWindow.end || toTimeString(bookingEnd));

        if (shiftStart >= shiftEnd) {
          setEditBookingError(`Shift end time must be after the start time for ${memberMeta.name}.`);
          setEditBookingSaving(false);
          return;
        }
        if (shiftStart > bookingStart || shiftEnd < bookingEnd) {
          setEditBookingError(`Shift for ${memberMeta.name} must cover the full booking time.`);
          setEditBookingSaving(false);
          return;
        }

        const shiftsRes = await api.get(
          `/api/team-member-shifts/team-member/${memberId}?start=${dayStart.toISOString()}&end=${dayEnd.toISOString()}`
        );
        const existingShifts = shiftsRes.data?.shifts || [];

        if (existingShifts.length === 0) {
          await api.post('/api/team-member-shifts', {
            teamMemberId: memberId,
            startTime: shiftStart.toISOString(),
            endTime: shiftEnd.toISOString()
          });
          continue;
        }

        const bookingStartMs = bookingStart.getTime();
        const bookingEndMs = bookingEnd.getTime();
        const bestShift = existingShifts.reduce((best, candidate) => {
          const cStart = new Date(candidate.startTime).getTime();
          const cEnd = new Date(candidate.endTime).getTime();
          const overlap = cStart <= bookingEndMs && cEnd >= bookingStartMs;
          const gap = overlap
            ? 0
            : Math.min(Math.abs(bookingStartMs - cEnd), Math.abs(cStart - bookingEndMs));
          if (!best || gap < best.gap) return { shift: candidate, gap };
          return best;
        }, null)?.shift;

        if (bestShift) {
          const extendedStart = new Date(
            Math.min(new Date(bestShift.startTime).getTime(), shiftStart.getTime())
          );
          const extendedEnd = new Date(
            Math.max(new Date(bestShift.endTime).getTime(), shiftEnd.getTime())
          );
          await api.put(`/api/team-member-shifts/${bestShift._id}`, {
            startTime: extendedStart.toISOString(),
            endTime: extendedEnd.toISOString(),
            breaks: bestShift.breaks || []
          });
        }
      }

      const assignmentIds = uniqueSelectedStaffIds;
      const requiresForceAssign = true;

      const res = await api.put(`/api/unified-bookings/${selectedBooking._id}`, {
        assignedTeamMembers: assignmentIds,
        ...(requiresForceAssign ? { forceAssign: true } : {}),
        supplierNotes: editSupplierNotes,
        location: {
          ...(selectedBooking.location || {}),
          type: editLocationType,
          address: editLocationAddress,
          videoLink: editLocationType === 'virtual' ? trimmedVideoLink : (selectedBooking.location?.videoLink || '')
        },
        notifyClient
      });
      if (res.data?.booking) setSelectedBooking(res.data.booking);
      setEditTeamMembersOpen(false);
      setBookingModalOpen(false);
      if (notifyClient && res.data?.clientNotified) {
        setEditBookingToast({ open: true, message: 'Saved. The couple has been notified by email and in-app.' });
      } else if (notifyClient) {
        setEditBookingToast({ open: true, message: 'Saved. No updates were sent to the couple.' });
      } else {
        setEditBookingToast({ open: true, message: 'Booking saved.' });
      }
      try {
        await fetchBookingData();
      } catch (_refreshError) {
        // Saving succeeded; ignore refresh errors so modal close/toast still happen.
      }
    } catch (error) {
      setEditBookingError(error.response?.data?.message || 'Failed to update booking.');
    } finally {
      setEditBookingSaving(false);
    }
  };

  const toTimeString = (dateLike) => {
    const d = new Date(dateLike);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const combineBookingDayWithTime = (bookingDate, hhmm) => {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    const day = new Date(bookingDate);
    day.setHours(h, m, 0, 0);
    return day;
  };

  const startApproveDialogForBooking = async (bookingRecord) => {
    if (!bookingRecord) return;
    setApproveError('');
    setApproveLoading(true);
    setApproveDialogOpen(true);
    setApproveShiftStart(toTimeString(bookingRecord.startTime));
    setApproveShiftEnd(toTimeString(bookingRecord.endTime));
    try {
      const res = await api.get(`/api/unified-bookings/${bookingRecord._id}/allowed-team-members`);
      const list = res.data?.allowedTeamMembers || [];
      const hasRestriction = !!res.data?.hasRestriction;
      setApproveAllowed(list);
      setApproveHasRestriction(hasRestriction);

      const preAssignedId = bookingRecord.assignedTeamMembers?.[0]?.member?._id
        || bookingRecord.assignedTeamMembers?.[0]?.member;
      if (preAssignedId && list.some(m => m.id?.toString() === preAssignedId.toString())) {
        setApproveSelectedId(preAssignedId.toString());
      } else if (list.length === 1) {
        setApproveSelectedId(list[0].id.toString());
      } else {
        setApproveSelectedId('');
      }
    } catch (err) {
      setApproveError(err.response?.data?.message || 'Failed to load allowed team members.');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleApproveClick = () => {
    void startApproveDialogForBooking(selectedBooking);
  };

  useEffect(() => {
    const bookingId = location.state?.openBookingApprove;
    if (!bookingId || loading || !allBookings.length) return;
    const match = allBookings.find((b) => String(b._id) === String(bookingId));
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    if (!match) return;
    setSelectedBooking(match);
    setBookingModalOpen(true);
    const pendingFuture = match.status === 'pending' && new Date(match.startTime) >= new Date();
    if (pendingFuture) {
      queueMicrotask(() => {
        void startApproveDialogForBooking(match);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deep link runs when bookings load
  }, [loading, allBookings, location.state?.openBookingApprove]);

  const closeApproveDialog = () => {
    setApproveDialogOpen(false);
    setApproveError('');
    setApproveAllowed([]);
    setApproveHasRestriction(false);
    setApproveSelectedId('');
    setApproveSubmitting(false);
  };

  const handleApproveSubmit = async () => {
    if (!selectedBooking || !approveSelectedId) return;
    const selectedMember = approveAllowed.find(m => m.id?.toString() === approveSelectedId);
    if (!selectedMember) return;
    setApproveError('');
    setApproveSubmitting(true);
    try {
      // If the selected member has no shift covering the booking, create one inline
      if (!selectedMember.hasShiftCoveringBooking) {
        if (!approveShiftStart || !approveShiftEnd) {
          setApproveError('Please provide a shift start and end time.');
          setApproveSubmitting(false);
          return;
        }
        const shiftStart = combineBookingDayWithTime(selectedBooking.startTime, approveShiftStart);
        const shiftEnd = combineBookingDayWithTime(selectedBooking.startTime, approveShiftEnd);
        if (shiftStart >= shiftEnd) {
          setApproveError('Shift end time must be after the start time.');
          setApproveSubmitting(false);
          return;
        }
        if (shiftStart > new Date(selectedBooking.startTime) || shiftEnd < new Date(selectedBooking.endTime)) {
          setApproveError('The shift must cover the full appointment time.');
          setApproveSubmitting(false);
          return;
        }
        await api.post('/api/team-member-shifts', {
          teamMemberId: approveSelectedId,
          startTime: shiftStart.toISOString(),
          endTime: shiftEnd.toISOString()
        });
      }

      const res = await api.put(`/api/unified-bookings/${selectedBooking._id}/status`, {
        status: 'confirmed',
        notifyClient: true,
        assignedTeamMembers: [approveSelectedId]
      });

      const suggest = Boolean(res.data?.suggestAddToCrm);
      const ctx =
        suggest && res.data
          ? {
              bookingId: res.data._id || selectedBooking._id,
              name: res.data.manualBookingDetails?.clientName || '',
              email: res.data.manualBookingDetails?.clientEmail || '',
              phone: res.data.manualBookingDetails?.clientPhone || ''
            }
          : null;

      await fetchBookingData();
      closeApproveDialog();
      setBookingModalOpen(false);
      setSelectedBooking(null);

      if (suggest && ctx) {
        setCrmAfterApproveContext(ctx);
        setCrmAfterApproveError('');
        setCrmAfterApproveOpen(true);
      }
    } catch (err) {
      setApproveError(err.response?.data?.message || 'Failed to approve booking.');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleAddToCouplesAfterApprove = async () => {
    if (!crmAfterApproveContext?.bookingId) return;
    setCrmAfterApproveSubmitting(true);
    setCrmAfterApproveError('');
    try {
      const res = await api.post(`/api/unified-bookings/${crmAfterApproveContext.bookingId}/add-to-couples`, {
        name: crmAfterApproveContext.name,
        email: crmAfterApproveContext.email,
        phone: crmAfterApproveContext.phone
      });
      setCrmAfterApproveOpen(false);
      setCrmAfterApproveContext(null);
      const leadId = res.data?.lead?._id;
      if (leadId) {
        navigate(`/supplier/crm/leads/${leadId}`);
      } else {
        navigate('/supplier/crm');
      }
    } catch (err) {
      setCrmAfterApproveError(err.response?.data?.message || 'Could not add this couple right now.');
    } finally {
      setCrmAfterApproveSubmitting(false);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const _groupBookingsByDay = (bookings) => {
    const grouped = {};
    bookings.forEach(booking => {
      const date = new Date(booking.startTime);
      const dayKey = date.toDateString();
      if (!grouped[dayKey]) {
        grouped[dayKey] = {
          date: date,
          bookings: []
        };
      }
      grouped[dayKey].bookings.push(booking);
    });
    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };


  const handleCreateNewClick = (event) => {
    setCreateNewAnchorEl(event.currentTarget);
  };

  const handleCreateNewClose = () => {
    setCreateNewAnchorEl(null);
  };

  const handleCreateNewAppointment = () => {
    handleCreateNewClose();
    if (appointmentTypesCount === 0) {
      setNoAppointmentTypesModalOpen(true);
      return;
    }
    navigate('/supplier/bookings/new');
  };

  const handleCreateNewEvent = () => {
    handleCreateNewClose();
    setSelectedEvent(null);
    setShowEventForm(true);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              Booking Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your appointments and booking requests
            </Typography>
          </Box>
          
          {/* Pending Appointments + Booking Management actions (grouped right) */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setPendingAppointmentsModalOpen(true)}
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: '#E91E63',
                color: 'white',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#C2185B',
                },
              }}
            >
              <Badge
                badgeContent={pendingRequests.length + thisWeekBookings.length}
                color="error"
                sx={{
                  mr: 1,
                  '& .MuiBadge-badge': {
                    backgroundColor: '#f44336',
                    color: 'white',
                  }
                }}
              >
                <CalendarCheck size={20} />
              </Badge>
              {pendingRequests.length + thisWeekBookings.length} Pending Appointment Request{(pendingRequests.length + thisWeekBookings.length) !== 1 ? 's' : ''}
            </Button>

            <Button
              variant="outlined"
              startIcon={<SlidersHorizontal size={20} />}
              onClick={() => navigate('/supplier/bookings/settings')}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#000',
                color: '#000',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Booking Management
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Main Content - Always show calendar */}
      {activeView === 'calendar' && (
        <Card sx={{ overflow: 'visible' }}>
          <CardContent sx={{ p: 0, overflow: 'visible' }}>
            <UnifiedCalendar
              userType="supplier"
              businessId={selectedBusiness?.business._id}
              onEventClick={handleCalendarEventClick}
              onBookingClick={handleViewBooking}
              onBookingChanged={handleCalendarBookingChanged}
              refreshToken={calendarRefresh}
              onCreateNewClick={handleCreateNewClick}
              onCreateNewAppointment={handleCreateNewAppointment}
              onCreateNewEvent={handleCreateNewEvent}
            />
          </CardContent>
        </Card>
      )}

      {/* Mobile bottom actions - displayed directly under calendar card */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          mt: 2,
          gap: 1,
        }}
      >
        <Button
          sx={{
            flex: 1,
            py: 1.5,
            borderRadius: 2,
          }}
          variant="contained"
          onClick={() => navigate('/supplier/schedules')}
          startIcon={<Plus size={18} />}
        >
          Add Shifts
        </Button>
        <Button
          sx={{
            flex: 1,
            py: 1.5,
            borderRadius: 2,
          }}
          variant="contained"
          onClick={() => navigate('/supplier/bookings/settings')}
          startIcon={<Gear size={18} />}
        >
          Settings
        </Button>
      </Box>

      {/* Event Form Modal - Available from header button */}
      {showEventForm && (
        <EventForm
          event={selectedEvent}
          onSuccess={() => {
            fetchEvents();
            setCalendarRefresh(prev => prev + 1);
            setShowEventForm(false);
            setSelectedEvent(null);
          }}
          onClose={() => {
            setShowEventForm(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Booking Details Modal */}
      <Dialog 
        open={bookingModalOpen} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Calendar size={24} />
              <Typography variant="h6">
                Booking Details
              </Typography>
              {selectedBooking && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: selectedBooking.status === 'confirmed' ? 'success.main' :
                             selectedBooking.status === 'pending' ? 'warning.main' :
                             selectedBooking.status === 'rejected' ? 'error.main' :
                             selectedBooking.status === 'rescheduled' && selectedBooking.requiresClientConfirmation ? 'warning.main' : 'grey.500'
                  }} />
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {selectedBooking.status === 'rescheduled' && selectedBooking.requiresClientConfirmation
                      ? 'Rescheduled – Pending'
                      : selectedBooking.status}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Service
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedBooking.serviceType?.name || 'Appointment'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date & Time
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedBooking.startTime)} at {formatTime(selectedBooking.startTime)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Client
                  </Typography>
                  {(() => {
                    // Always check for manual booking details first (even if isManualBooking flag is missing)
                    const manualDetails = selectedBooking.manualBookingDetails;
                    const hasManualDetails = manualDetails && (manualDetails.clientName || manualDetails.clientEmail);

                    if (hasManualDetails) {
                      return (
                        <>
                          <Typography variant="body1">
                            {manualDetails.clientName || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {manualDetails.clientEmail || ''}
                            {manualDetails.clientPhone && ` • ${manualDetails.clientPhone}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {selectedBooking.isPublicEmbedBooking ? 'Form Embed' : 'Manual booking'}
                          </Typography>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <Typography variant="body1">
                            {getBookingGuestDisplayName(selectedBooking)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getBookingGuestEmailLine(selectedBooking)}
                          </Typography>
                        </>
                      );
                    }
                  })()}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Assigned Team Member
                  </Typography>
                  {selectedBooking.assignedTeamMembers && selectedBooking.assignedTeamMembers.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedBooking.assignedTeamMembers.map((member, index) => {
                        const fromMember = member.member?.user?.firstName
                          ? `${member.member.user.firstName} ${member.member.user.lastName || ''}`.trim()
                          : member.member?.staffMemberName;
                        const fromStaffMember = member.staffMember?.name
                          || (member.staffMember?.user?.firstName
                            ? `${member.staffMember.user.firstName} ${member.staffMember.user.lastName || ''}`.trim()
                            : null);
                        const displayName = fromMember || fromStaffMember || 'Unknown';
                        return (
                          <Chip
                            key={index}
                            label={displayName}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No team member assigned
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedBooking.location?.type === 'on_site' && 'On-site'}
                    {selectedBooking.location?.type === 'off_site' && 'Off-site'}
                    {selectedBooking.location?.type === 'virtual' && 'Virtual'}
                  </Typography>
                  {selectedBooking.location?.address && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.location.address}
                    </Typography>
                  )}
                  {selectedBooking.location?.type === 'virtual' && selectedBooking.location?.videoLink && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.location.videoLink}
                    </Typography>
                  )}
                </Grid>

                {selectedBooking.supplierNotes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Internal notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedBooking.supplierNotes}
                    </Typography>
                  </Grid>
                )}
                
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, justifyContent: 'flex-start', gap: 1 }}>
          {/* Missed appointments (pending bookings that are now in the past) - show only Adjust and Decline */}
          {selectedBooking?.status === 'pending' && new Date(selectedBooking.startTime) < new Date() && (
            <>
              <Button
                variant="outlined"
                color="info"
                onClick={() => handleAdjustClick()}
              >
                Adjust
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleBookingAction(selectedBooking._id, 'rejected')}
              >
                Decline
              </Button>
            </>
          )}

          {/* Regular pending bookings (future) - show Approve, Adjust, Decline */}
          {selectedBooking?.status === 'pending' && new Date(selectedBooking.startTime) >= new Date() && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveClick}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={() => handleAdjustClick()}
              >
                Adjust
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleBookingAction(selectedBooking._id, 'rejected')}
              >
                Decline
              </Button>
            </>
          )}
          
          {(selectedBooking?.status === 'confirmed' || selectedBooking?.status === 'rescheduled') && (
            <>
              <Button
                variant="outlined"
                color="info"
                onClick={() => handleAdjustClick()}
              >
                Reschedule
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleEditTeamMembers}
                startIcon={<Users size={16} />}
              >
                Edit booking
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleBookingAction(selectedBooking._id, 'cancelled')}
              >
                Cancel Booking
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Adjustment Modal */}
      <Dialog 
        open={adjustmentModalOpen} 
        onClose={() => {
          setAdjustmentModalOpen(false);
          setRescheduleStaffMemberId('');
          setRescheduleSelectedDate(null);
          setRescheduleSlots([]);
          setRescheduleBookingId(null);
          rescheduleBookingIdRef.current = null;
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarCheck size={24} />
              <Typography variant="h6">
                Reschedule Booking
              </Typography>
            </Box>
            <IconButton
              onClick={() => setAdjustmentModalOpen(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Staff Member
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={rescheduleStaffMemberId}
                    onChange={(e) => {
                      setRescheduleStaffMemberId(e.target.value);
                      setNewStartTime('');
                      setNewEndTime('');
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">Select staff member</MenuItem>
                    {rescheduleStaffMembers.map((sm) => {
                      const name = sm.user ? `${sm.user.firstName || ''} ${sm.user.lastName || ''}`.trim() || sm.name : sm.name || 'Staff';
                      return (
                        <MenuItem key={sm._id} value={sm._id}>{name}</MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    value={rescheduleSelectedDate}
                    onChange={(d) => {
                      setRescheduleSelectedDate(d);
                      setNewStartTime('');
                      setNewEndTime('');
                    }}
                    minDate={new Date()}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Available Time Slots
                </Typography>
                {loadingRescheduleSlots ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading slots…</Typography>
                  </Box>
                ) : rescheduleSlots.length === 0 && rescheduleStaffMemberId && rescheduleSelectedDate ? (
                  <Typography variant="body2" color="text.secondary">
                    No available slots on this date. The business may be closed that day or there are no times within opening hours for this appointment length.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {rescheduleSlots.map((slot) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      const label = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      const isSelected = newStartTime === slot.startTime;
                      return (
                        <Button
                          key={slot.startTime}
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                          size="small"
                          onClick={() => {
                            setNewStartTime(slot.startTime);
                            setNewEndTime(slot.endTime);
                          }}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            py: 0.75,
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Reason for Rescheduling
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="Please explain why this booking needs to be rescheduled..."
                  variant="outlined"
                />
              </Grid>
              
              {adjustmentSubmitError && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => setAdjustmentSubmitError('')}>
                    {adjustmentSubmitError}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    The client will be notified via email and in-app notification about this rescheduling request.
                    They will need to confirm the new time.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setAdjustmentModalOpen(false)} disabled={adjustmentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleAdjustmentSubmit}
            disabled={!newStartTime || !adjustmentReason || adjustmentSubmitting}
          >
            {adjustmentSubmitting ? 'Sending…' : 'Send Reschedule Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking editing modal */}
      <Dialog 
        open={editTeamMembersOpen} 
        onClose={() => setEditTeamMembersOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Users size={24} />
              <Typography variant="h6">
                Edit booking
              </Typography>
            </Box>
            <IconButton
              onClick={() => setEditTeamMembersOpen(false)}
              size="small"
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Location and notes
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-location-type-label">Location type</InputLabel>
                  <Select
                    labelId="edit-location-type-label"
                    value={editLocationType}
                    label="Location type"
                    onChange={(e) => setEditLocationType(e.target.value)}
                  >
                    <MenuItem value="on_site">On-site</MenuItem>
                    <MenuItem value="off_site">Off-site</MenuItem>
                    <MenuItem value="virtual">Virtual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {editLocationType === 'virtual' ? (
                  <TextField
                    fullWidth
                    size="small"
                    label="Video call link"
                    placeholder="https://meet.example.com/..."
                    value={editVideoLink}
                    onChange={(e) => setEditVideoLink(e.target.value)}
                    helperText="Use Save and update couple to email the meeting link."
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Location"
                    placeholder="Address or meeting point"
                    value={editLocationAddress}
                    onChange={(e) => setEditLocationAddress(e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Internal notes"
                  multiline
                  rows={3}
                  value={editSupplierNotes}
                  onChange={(e) => setEditSupplierNotes(e.target.value)}
                  placeholder="Private notes for your team. Not shared with the couple."
                />
              </Grid>
            </Grid>

            {editBookingError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditBookingError('')}>
                {editBookingError}
              </Alert>
            )}
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Team members
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select one or more team members. Currently assigned people are pre-selected.
            </Typography>
            
            {availableTeamMembersForEdit.length === 0 ? (
              <Alert severity="warning">
                No team members are configured for this appointment type. Update allowed team members in Booking Management.
              </Alert>
            ) : (
              <>
                <Grid container spacing={2}>
                  {availableTeamMembersForEdit.map((member) => {
                    const memberId = String(member.id);
                    const isSelected = selectedTeamMembersForEdit.includes(memberId);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={memberId}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            border: isSelected ? 2 : 1,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            '&:hover': { borderColor: 'primary.main' }
                          }}
                          onClick={() => {
                            setSelectedTeamMembersForEdit((prev) => {
                              const exists = prev.includes(memberId);
                              return exists ? prev.filter((id) => id !== memberId) : [...prev, memberId];
                            });
                            if (!member.hasShiftCoveringBooking) {
                              setEditShiftWindowsByMember((prev) => ({
                                ...prev,
                                [memberId]: prev[memberId] || {
                                  start: toTimeString(selectedBooking.startTime),
                                  end: toTimeString(selectedBooking.endTime)
                                }
                              }));
                            }
                          }}
                        >
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {member.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {member.role}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <CheckCircle size={16} color={isSelected ? '#4caf50' : '#ccc'} />
                              <Typography variant="body2">
                                {isSelected ? 'Selected' : 'Tap to select'}
                              </Typography>
                            </Box>
                            {!member.hasShiftCoveringBooking && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
                                Needs shift coverage
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {selectedTeamMembersForEdit.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This booking has no assigned team members. Select at least one before saving.
                  </Alert>
                )}

                {selectedTeamMembersForEdit
                  .map((selectedId) => availableTeamMembersForEdit.find((member) => String(member.id) === String(selectedId)))
                  .filter((member) => member && !member.hasShiftCoveringBooking)
                  .map((member) => {
                    const memberId = String(member.id);
                    const shiftWindow = editShiftWindowsByMember[memberId] || {};
                    return (
                      <Box key={`shift-window-${memberId}`} sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          {member.hasAnyShiftOnDay
                            ? `${member.name} has a shift on this day, so it will be extended to cover this booking.`
                            : `${member.name} has no shift on this day, so a new shift will be added.`}
                        </Alert>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            type="time"
                            size="small"
                            fullWidth
                            label={`${member.name} shift start`}
                            InputLabelProps={{ shrink: true }}
                            value={shiftWindow.start || toTimeString(selectedBooking.startTime)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditShiftWindowsByMember((prev) => ({
                                ...prev,
                                [memberId]: {
                                  start: value,
                                  end: prev[memberId]?.end || toTimeString(selectedBooking.endTime)
                                }
                              }));
                            }}
                          />
                          <TextField
                            type="time"
                            size="small"
                            fullWidth
                            label={`${member.name} shift end`}
                            InputLabelProps={{ shrink: true }}
                            value={shiftWindow.end || toTimeString(selectedBooking.endTime)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditShiftWindowsByMember((prev) => ({
                                ...prev,
                                [memberId]: {
                                  start: prev[memberId]?.start || toTimeString(selectedBooking.startTime),
                                  end: value
                                }
                              }));
                            }}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
              </>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setEditTeamMembersOpen(false)} disabled={editBookingSaving}>
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleSaveBookingEdit(false)}
            disabled={editBookingSaving}
          >
            {editBookingSaving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSaveBookingEdit(true)}
            disabled={editBookingSaving || !bookingHasCoupleContact(selectedBooking)}
          >
            {editBookingSaving ? 'Saving…' : 'Save and update couple'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={editBookingToast.open}
        autoHideDuration={4000}
        onClose={() => setEditBookingToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setEditBookingToast({ open: false, message: '' })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {editBookingToast.message}
        </Alert>
      </Snackbar>

      {/* Calendar Sync Settings Dialog */}
      <CalendarSyncSettings
        open={showSyncSettings}
        onClose={() => setShowSyncSettings(false)}
        businessId={selectedBusiness?.business._id}
      />

      {/* Pending Appointments Modal - includes pending and upcoming */}
      <Dialog
        open={pendingAppointmentsModalOpen}
        onClose={() => setPendingAppointmentsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarCheck size={24} />
              <Typography variant="h6">
                Appointment Requests
              </Typography>
              <Badge badgeContent={pendingRequests.length + thisWeekBookings.length} color="error">
                <Box />
              </Badge>
            </Box>
            <IconButton
              onClick={() => setPendingAppointmentsModalOpen(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pendingRequests.length === 0 && thisWeekBookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CalendarBlank size={48} color="text.secondary" />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                No appointment requests yet
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Pending Requests Section */}
              {pendingRequests.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'warning.main' }}>
                    Pending Requests ({pendingRequests.length})
                  </Typography>
                  <List sx={{ width: '100%', p: 0 }}>
                    {pendingRequestsPageItems.map((booking) => (
                      <ListItem 
                        key={booking._id} 
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 1,
                          mx: 0,
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          width: '100%',
                          boxSizing: 'border-box',
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                              {booking.serviceType?.name || 'Appointment'}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                {formatDate(booking.startTime)} at {formatTime(booking.startTime)}
                              </Box>
                              {getBookingGuestDisplayName(booking) && (
                                <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block' }}>
                                  {getBookingGuestDisplayName(booking)}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1, gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            startIcon={<Eye size={16} />}
                            onClick={() => {
                              handleViewBooking(booking);
                              setPendingAppointmentsModalOpen(false);
                            }}
                            sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                  {pendingRequests.length > bookingsModalPageSize && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={pendingModalPageCount}
                        page={pendingModalPage}
                        onChange={(_, page) => setPendingModalPage(page)}
                        color="primary"
                        shape="rounded"
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Upcoming Appointments Section */}
              {thisWeekBookings.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                    Upcoming Appointments ({thisWeekBookings.length})
                  </Typography>
                  <List sx={{ width: '100%', p: 0 }}>
                    {upcomingBookingsPageItems.map((booking) => (
                      <ListItem 
                        key={booking._id} 
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 1,
                          mx: 0,
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          width: '100%',
                          boxSizing: 'border-box',
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                                {booking.serviceType?.name || 'Appointment'}
                              </Box>
                              <Chip
                                size="small"
                                label={booking.status === 'rescheduled' && booking.requiresClientConfirmation ? 'Rescheduled – Pending' : booking.status}
                                color={booking.status === 'confirmed' ? 'success' : booking.status === 'rescheduled' ? 'warning' : 'default'}
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                {formatDate(booking.startTime)} at {formatTime(booking.startTime)}
                              </Box>
                              {getBookingGuestDisplayName(booking) && (
                                <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', display: 'block' }}>
                                  {getBookingGuestDisplayName(booking)}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1, gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            startIcon={<Eye size={16} />}
                            onClick={() => {
                              handleViewBooking(booking);
                              setPendingAppointmentsModalOpen(false);
                            }}
                            sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                  {thisWeekBookings.length > bookingsModalPageSize && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={upcomingModalPageCount}
                        page={upcomingModalPage}
                        onChange={(_, page) => setUpcomingModalPage(page)}
                        color="primary"
                        shape="rounded"
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAppointmentsModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Booking Dialog - allowed team members + inline shift capture */}
      <Dialog
        open={approveDialogOpen}
        onClose={approveSubmitting ? undefined : closeApproveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Appointment</DialogTitle>
        <DialogContent>
          {approveLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {approveError && (
                <Alert severity="error" sx={{ mb: 2 }}>{approveError}</Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {approveHasRestriction
                  ? 'Only team members permitted for this appointment type are shown.'
                  : 'All active team members are available for this appointment type.'}
              </Typography>

              {approveAllowed.length === 0 ? (
                <Alert severity="warning">
                  No team members are permitted for this appointment type. Update the appointment type&apos;s allowed team members in Booking Management.
                </Alert>
              ) : (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Team member</InputLabel>
                  <Select
                    label="Team member"
                    value={approveSelectedId}
                    onChange={(e) => setApproveSelectedId(e.target.value)}
                  >
                    {approveAllowed.map((m) => (
                      <MenuItem key={m.id} value={m.id.toString()}>
                        {m.name}
                        {!m.hasShiftCoveringBooking && (
                          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            (needs shift)
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {approveSelectedId && (() => {
                const member = approveAllowed.find(m => m.id?.toString() === approveSelectedId);
                if (!member) return null;
                if (member.hasShiftCoveringBooking) {
                  return (
                    <Alert severity="success">
                      {member.name} is scheduled to cover this appointment.
                    </Alert>
                  );
                }
                return (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {member.name} doesn&apos;t have a shift this day. Add their shift below, or use Adjust to reschedule instead.
                    </Alert>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Shift start"
                        type="time"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={approveShiftStart}
                        onChange={(e) => setApproveShiftStart(e.target.value)}
                      />
                      <TextField
                        label="Shift end"
                        type="time"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={approveShiftEnd}
                        onChange={(e) => setApproveShiftEnd(e.target.value)}
                      />
                    </Stack>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveDialog} disabled={approveSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSubmit}
            disabled={approveLoading || approveSubmitting || !approveSelectedId || approveAllowed.length === 0}
          >
            {approveSubmitting ? 'Approving…' : 'Approve booking'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noAppointmentTypesModalOpen}
        onClose={() => setNoAppointmentTypesModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add an appointment type first</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You need at least one appointment type before you can create an appointment. Set one up in Booking Management, then come back here to add your first appointment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoAppointmentTypesModalOpen(false)}>Not now</Button>
          <Button
            variant="contained"
            onClick={() => {
              setNoAppointmentTypesModalOpen(false);
              navigate('/supplier/bookings/settings');
            }}
          >
            Go to Booking Management
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={crmAfterApproveOpen} onClose={() => setCrmAfterApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add this couple to My Couples?</DialogTitle>
        <DialogContent>
          {crmAfterApproveError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {crmAfterApproveError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            This appointment is not linked to anyone in your pipeline yet. Adding a couple keeps notes, follow-ups, and future bookings in one place.
          </Typography>
          {crmAfterApproveContext?.name ? (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Name:</strong> {crmAfterApproveContext.name}
            </Typography>
          ) : null}
          {crmAfterApproveContext?.email ? (
            <Typography variant="body2">
              <strong>Email:</strong> {crmAfterApproveContext.email}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrmAfterApproveOpen(false)} disabled={crmAfterApproveSubmitting}>Not now</Button>
          <Button
            variant="contained"
            onClick={handleAddToCouplesAfterApprove}
            disabled={crmAfterApproveSubmitting}
          >
            {crmAfterApproveSubmitting ? 'Adding…' : 'Add to couples'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default UnifiedBookingDashboard;
